import React from 'react';
import { Layers, AlertCircle } from 'lucide-react';

/**
 * Компонент секции материалов и конструкций здания
 */
const BuildingEnvelopeSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  buildingMaterials,
  windowTypes
}) => {
  return (
    <div className="mb-6 bg-gray-50 dark:bg-gray-800/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Layers className="w-5 h-5 mr-2 text-red-500" />
        Материалы и конструкции
      </h3>
      
      {/* Стеновые конструкции */}
      <div className="mb-5">
        <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-3 flex items-center">
          Стены
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Материал стен */}
          <div>
            <label htmlFor="wallMaterial" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Материал стен
            </label>
            <select
              id="wallMaterial"
              name="wallMaterial"
              value={formData.wallMaterial}
              onChange={handleInputChange}
              className="form-select w-full"
            >
              <optgroup label="Кирпич">
                <option value="brick_noninsulated">Кирпич без утепления</option>
                <option value="brick_insulated">Кирпич с утеплителем</option>
              </optgroup>
              <optgroup label="Бетон">
                <option value="concrete_noninsulated">Бетон без утепления</option>
                <option value="concrete_insulated">Бетон с утеплителем</option>
                <option value="reinforced_concrete">Железобетонные панели</option>
              </optgroup>
              <optgroup label="Дерево">
                <option value="wood_log">Бревно</option>
                <option value="wood_timber">Брус</option>
                <option value="wood_frame">Каркасная конструкция</option>
              </optgroup>
              <optgroup label="Другие материалы">
                <option value="aerated_concrete">Газобетон/пенобетон</option>
                <option value="foam_blocks">Пенополистирольные блоки</option>
                <option value="ceramic_blocks">Керамические блоки</option>
              </optgroup>
            </select>
          </div>
          
          {/* Материал крыши/потолка */}
          <div>
            <label htmlFor="roofMaterial" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Материал {formData.buildingType === 'apartment' ? 'перекрытия' : 'крыши'}
            </label>
            <select
              id="roofMaterial"
              name="roofMaterial"
              value={formData.roofMaterial}
              onChange={handleInputChange}
              className="form-select w-full"
              disabled={formData.roofType === 'adjacent'}
            >
              <option value="concrete_noninsulated">Железобетон без утепления</option>
              <option value="concrete_insulated">Железобетон с утеплителем</option>
              <option value="wood_noninsulated">Деревянное перекрытие без утепления</option>
              <option value="wood_insulated">Деревянное перекрытие с утеплителем</option>
            </select>
          </div>
          
          {/* Толщина утеплителя крыши/потолка */}
          <div className={formData.roofType === 'insulated' || formData.roofType === 'flat' ? '' : 'hidden'}>
            <label htmlFor="roofInsulationThickness" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Толщина утеплителя {formData.buildingType === 'apartment' ? 'потолка' : 'крыши'}, мм
            </label>
            <input
              type="text"
              id="roofInsulationThickness"
              name="roofInsulationThickness"
              value={formData.roofInsulationThickness}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.roofInsulationThickness ? 'input-error' : ''}`}
              placeholder="Например: 150"
              disabled={formData.roofType !== 'insulated' && formData.roofType !== 'flat'}
            />
            {errors.roofInsulationThickness && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.roofInsulationThickness}
              </p>
            )}
          </div>
        </div>
      </div>
      
      {/* Пол */}
      <div className="mb-5">
        <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-3 flex items-center">
          Пол
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Тип пола */}
          <div>
            <label htmlFor="floorType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Тип конструкции
            </label>
            <select
              id="floorType"
              name="floorType"
              value={formData.floorType}
              onChange={handleInputChange}
              className="form-select w-full"
              disabled={formData.buildingType === 'apartment'}
            >
              <option value="exposed">Пол над холодным подвалом</option>
              <option value="ground">Пол по грунту</option>
              <option value="insulated">Утепленный пол</option>
              <option value="adjacent">Соседнее помещение снизу</option>
            </select>
            {formData.buildingType === 'apartment' && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Для квартир используется тип "Соседнее помещение снизу"
              </p>
            )}
          </div>
          
          {/* Материал пола */}
          <div>
            <label htmlFor="floorMaterial" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Материал перекрытия
            </label>
            <select
              id="floorMaterial"
              name="floorMaterial"
              value={formData.floorMaterial}
              onChange={handleInputChange}
              className="form-select w-full"
              disabled={formData.floorType === 'adjacent'}
            >
              <option value="concrete_noninsulated">Железобетон без утепления</option>
              <option value="concrete_insulated">Железобетон с утеплителем</option>
              <option value="wood_noninsulated">Деревянное перекрытие без утепления</option>
              <option value="wood_insulated">Деревянное перекрытие с утеплителем</option>
            </select>
          </div>
          
          {/* Толщина утеплителя пола */}
          <div className={formData.floorType === 'insulated' ? '' : 'hidden'}>
            <label htmlFor="floorInsulationThickness" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Толщина утеплителя пола, мм
            </label>
            <input
              type="text"
              id="floorInsulationThickness"
              name="floorInsulationThickness"
              value={formData.floorInsulationThickness}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.floorInsulationThickness ? 'input-error' : ''}`}
              placeholder="Например: 100"
              disabled={formData.floorType !== 'insulated'}
            />
            {errors.floorInsulationThickness && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.floorInsulationThickness}
              </p>
            )}
          </div>
        </div>
      </div>
      
      {/* Информационный блок */}
      <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800 flex">
        <AlertCircle className="w-5 h-5 text-blue-500 dark:text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
        <div>
          <h4 className="text-sm text-blue-700 dark:text-blue-300 font-medium mb-1">
            Рекомендации по теплоизоляции
          </h4>
          <p className="text-xs text-blue-600 dark:text-blue-400">
            Для средней полосы России рекомендуемая толщина утеплителя: для стен — 100-150 мм, 
            для крыши — 200-250 мм, для пола — 100-150 мм. Это позволит достичь показателей теплопередачи,
            соответствующих современным нормам энергоэффективности.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BuildingEnvelopeSection;